import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Download, Clock, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../context/AuthContext';
import { conversionAPI } from '../services/api';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [conversionHistory, setConversionHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const fetchHistory = async () => {
      try {
        const data = await conversionAPI.getHistory();
        setConversionHistory(data.conversions);
      } catch (error) {
        console.error('Error fetching history:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [user, navigate]);

  if (!user) {
    return null;
  }

  const usagePercentage = (user.pages_used / user.pages_limit) * 100;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'processing':
        return <Clock className="w-5 h-5 text-yellow-500 animate-spin" />;
      case 'failed':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 via-pink-100 to-pink-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Welcome back, {user.name}!
          </h1>
          <p className="text-gray-700">Manage your conversions and subscription</p>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="border-pink-200 bg-white/60 backdrop-blur-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                Pages Used
              </CardTitle>
              <FileText className="w-4 h-4 text-pink-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">
                {user.pages_used} / {user.pages_limit}
              </div>
              <Progress value={usagePercentage} className="mt-3 h-2" />
              <p className="text-xs text-gray-500 mt-2">
                {user.next_reset ? `Resets on ${new Date(user.next_reset).toLocaleDateString()}` : 'Daily reset'}
              </p>
            </CardContent>
          </Card>

          <Card className="border-pink-200 bg-white/60 backdrop-blur-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-700">
                Subscription
              </CardTitle>
              <CheckCircle2 className="w-4 h-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900 capitalize">
                {user.tier}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Status: {user.subscriptionStatus}
              </p>
            </CardContent>
          </Card>

          <Card className="border-pink-200 bg-white/60 backdrop-blur-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-700">
                Total Conversions
              </CardTitle>
              <Download className="w-4 h-4 text-pink-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">
                {conversionHistory.length}
              </div>
              <p className="text-xs text-gray-500 mt-2">All time</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="mb-8 border-pink-200 bg-white/60 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-gray-900">Quick Actions</CardTitle>
            <CardDescription className="text-gray-700">Common tasks and shortcuts</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-4">
            <Button
              onClick={() => navigate('/')}
              className="bg-gradient-to-r from-pink-400 to-pink-300 hover:from-pink-500 hover:to-pink-400 text-white"
            >
              <FileText className="w-4 h-4 mr-2" />
              Convert New PDF
            </Button>
            <Button
              onClick={() => navigate('/pricing')}
              variant="outline"
              className="border-pink-200 hover:border-pink-300 hover:bg-pink-50"
            >
              Upgrade Plan
            </Button>
          </CardContent>
        </Card>

        {/* Conversion History */}
        <Card className="border-pink-200 bg-white/60 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-gray-900">Recent Conversions</CardTitle>
            <CardDescription className="text-gray-700">Your conversion history and downloads</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <Clock className="w-8 h-8 text-pink-500 animate-spin mx-auto mb-2" />
                <p className="text-gray-600">Loading history...</p>
              </div>
            ) : conversionHistory.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-600">No conversions yet</p>
                <p className="text-sm text-gray-500 mt-1">Upload a PDF to get started</p>
              </div>
            ) : (
              <div className="space-y-4">
                {conversionHistory.map((conversion) => (
                  <div
                    key={conversion.id}
                    className="flex items-center justify-between p-4 bg-white rounded-lg border border-pink-100 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center space-x-4">
                      {getStatusIcon(conversion.status)}
                      <div>
                        <p className="font-medium text-gray-900">{conversion.fileName}</p>
                        <p className="text-sm text-gray-500">
                          {conversion.pages} pages • {new Date(conversion.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    {conversion.status === 'completed' && conversion.downloadUrl && (
                      <a href={conversionAPI.downloadFile(conversion.id)} download>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-pink-200 hover:border-pink-300 hover:bg-pink-50"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download
                        </Button>
                      </a>
                    )}
                    {conversion.status === 'processing' && (
                      <span className="text-sm text-yellow-600 font-medium">Processing...</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;